import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to detect opponent strategem plays and trigger animations
 * Uses action history to detect 'strategem-requested' actions from opponent
 * Animates black balls from opponent general to targeted units/generals
 * Works for both human opponents and bot opponents
 * 
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user's ID
 * @param {Function} registerAnimation - Function to register strategem animations
 * @param {Object} ghostUnitsRef - Ref to store ghost units (from useStrategemTargetAnimations)
 * @param {Array} actionHistory - Array of detected actions (from actionDetection.js)
 */
export const useOpponentStrategemAnimations = (gameState, userId, registerAnimation, ghostUnitsRef, actionHistory) => {
  const processedEffectsRef = useRef(new Set());
  const animationCounterRef = useRef(0);

  // Get opponent general position (for animation start point)
  const getOpponentGeneralPosition = useCallback(() => {
    const generalElement = document.querySelector('[data-zone="opponent_general"]');
    if (generalElement) {
      const rect = generalElement.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }
    // Fallback position (top-right of screen where opponent general typically is)
    return { x: window.innerWidth - 200, y: 100 };
  }, []);

  // ==========================================
  // DETECT OPPONENT STRATEGEMS FROM GAME STATE METADATA (PRIMARY)
  // ==========================================
  useEffect(() => {
    if (!gameState?.state || !userId || !registerAnimation) {
      return;
    }

    // ✅ NEW: Check for strategemAnimations in game state (metadata-based, perfect data)
    if (gameState.strategemAnimations && Array.isArray(gameState.strategemAnimations)) {
      const opponentAnimations = gameState.strategemAnimations.filter(anim => {
        // Only process animations from opponent (not current player)
        return String(anim.playerId) !== String(userId);
      });

      // ✅ CRITICAL FIX: Also process player strategem animations for zone-targeting strategems
      // This handles strategems like 54 (Repurposed Warhead) that affect both players
      // The backend metadata includes units from both players, which the frontend might have missed
      const playerAnimations = gameState.strategemAnimations.filter(anim => {
        // Only process animations from current player
        return String(anim.playerId) === String(userId);
      });

      if (opponentAnimations.length > 0) {
        const fromPosition = getOpponentGeneralPosition();
        
        opponentAnimations.forEach(animation => {
          const effectKey = `strategem-${animation.strategemInstanceId}-${animation.timestamp}`;
          
          if (processedEffectsRef.current.has(effectKey)) {
            return; // Already processed
          }
          
          processedEffectsRef.current.add(effectKey);
          animationCounterRef.current += 1;
          const animationKey = `opponent-strategem-${animation.strategemInstanceId}-${animationCounterRef.current}-${Date.now()}`;

          if (animation.targetType === 'general') {
            // General targeting
            registerAnimation({
              fromHand: { 
                instanceId: animation.strategemInstanceId, 
                position: fromPosition 
              },
              toSlot: { 
                zone: 'general', 
                index: null, 
                playerId: animation.targetPlayerId === userId ? 'your' : 'opponent'
              },
              targetType: 'general',
              strategemId: animation.strategemId,
              strategemInstanceId: animation.strategemInstanceId,
              animationKey,
              isOpponentStrategem: true
            });
          } else if (animation.targetType === 'unit' && animation.targetZone && animation.targetIndex !== null) {
            // Unit targeting - use perfect metadata
            // ✅ CRITICAL FIX: Convert zone prefix from strategem player's perspective to current user's perspective
            // Backend sends zones like 'your_front' or 'opponent_front' from the strategem player's perspective
            // We need to convert to the current user's perspective
            let targetZone = animation.targetZone;
            
            // If the target player is the current user, the zone should be 'your_'
            // If the target player is the opponent, the zone should be 'opponent_'
            const isTargetingCurrentUser = String(animation.targetPlayerId) === String(userId);
            const zoneName = targetZone.replace('your_', '').replace('opponent_', ''); // Extract base zone name
            const correctZone = isTargetingCurrentUser ? `your_${zoneName}` : `opponent_${zoneName}`;
            const targetPlayerId = isTargetingCurrentUser ? 'your' : 'opponent';
            
            // Store ghost unit if available
            if (animation.ghostUnit && ghostUnitsRef) {
              const slotKey = `${correctZone}_${animation.targetIndex}`;
              ghostUnitsRef.current.set(slotKey, {
                unit: animation.ghostUnit,
                animationKey: animationKey,
                targetInstanceId: animation.targetInstanceId
              });
            }
            
            registerAnimation({
              fromHand: { 
                instanceId: animation.strategemInstanceId, 
                position: fromPosition 
              },
              toSlot: { 
                zone: correctZone, 
                index: animation.targetIndex, 
                playerId: targetPlayerId
              },
              targetType: 'unit',
              strategemId: animation.strategemId,
              strategemInstanceId: animation.strategemInstanceId,
              targetInstanceId: animation.targetInstanceId,
              ghostUnit: animation.ghostUnit,
              animationKey,
              isOpponentStrategem: true
            });
          }
        });
        
        return; // Don't fall back to action history if we have metadata
      }

      // ✅ CRITICAL FIX: Process player strategem animations from backend metadata
      // This ensures ghost units are captured for all affected units (including both players for strategem 54)
      // Also handles general-targeting strategems like Kamikaze (314) that deal damage to general indirectly
      // The frontend may have already registered animations, but backend metadata includes ALL affected units
      // IMPORTANT: Backend sends zones from strategem player's perspective, so we need to convert to current user's perspective
      if (playerAnimations.length > 0) {
        // Get player general position for general-targeting animations
        const getPlayerGeneralPosition = () => {
          const generalElement = document.querySelector('[data-zone="player_general"]');
          if (generalElement) {
            const rect = generalElement.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2
            };
          }
          // Fallback position (bottom-left of screen where player general typically is)
          return { x: 200, y: 600 };
        };
        
        playerAnimations.forEach(animation => {
          // Handle general-targeting animations (e.g., Kamikaze dealing damage to enemy general)
          if (animation.targetType === 'general') {
            const effectKey = `player-strategem-general-${animation.strategemInstanceId}-${animation.targetPlayerId}-${animation.timestamp}`;
            
            if (processedEffectsRef.current.has(effectKey)) {
              return; // Already processed
            }
            
            processedEffectsRef.current.add(effectKey);
            animationCounterRef.current += 1;
            const animationKey = `player-strategem-general-${animation.strategemInstanceId}-${animationCounterRef.current}-${Date.now()}`;
            
            const fromPosition = getPlayerGeneralPosition();
            const targetPlayerId = animation.targetPlayerId === userId ? 'your' : 'opponent';
            
            
            registerAnimation({
              fromHand: { 
                instanceId: animation.strategemInstanceId, 
                position: fromPosition 
              },
              toSlot: { 
                zone: 'general', 
                index: null, 
                playerId: targetPlayerId
              },
              targetType: 'general',
              strategemId: animation.strategemId,
              strategemInstanceId: animation.strategemInstanceId,
              animationKey,
              isOpponentStrategem: false, // Player's own strategem
              effectType: 'damage' // General damage is always damage
            });
            
            return; // Don't process as ghost unit
          }
          
          // Handle unit-targeting animations (for ghost units)
          const effectKey = `player-strategem-ghost-${animation.strategemInstanceId}-${animation.targetZone}-${animation.targetIndex}-${animation.timestamp}`;
          
          if (processedEffectsRef.current.has(effectKey)) {
            return; // Already processed
          }
          
          processedEffectsRef.current.add(effectKey);

          if (animation.targetType === 'unit' && animation.targetZone && animation.targetIndex !== null && animation.ghostUnit) {
            // ✅ CRITICAL: Convert zone prefix from strategem player's perspective to current user's perspective
            // Backend sends zones like 'your_front' or 'opponent_front' from the strategem player's perspective
            // We need to convert to the current user's perspective
            let targetZone = animation.targetZone;
            
            // If the target player is the current user, the zone should be 'your_'
            // If the target player is the opponent, the zone should be 'opponent_'
            const isTargetingCurrentUser = String(animation.targetPlayerId) === String(userId);
            const zoneName = targetZone.replace('your_', '').replace('opponent_', ''); // Extract base zone name
            const correctZone = isTargetingCurrentUser ? `your_${zoneName}` : `opponent_${zoneName}`;
            
            if (ghostUnitsRef) {
              const slotKey = `${correctZone}_${animation.targetIndex}`;
              
              // Only store if not already present (prevent duplicates with frontend-captured ghosts)
              if (!ghostUnitsRef.current.has(slotKey)) {
                const backendAnimationKey = `backend-${animation.strategemInstanceId}-${animation.targetIndex}`;
                ghostUnitsRef.current.set(slotKey, {
                  unit: animation.ghostUnit,
                  animationKey: backendAnimationKey,
                  targetInstanceId: animation.targetInstanceId
                });
                
                // ✅ CRITICAL FIX: Add timeout cleanup for backend ghost units (fallback in case animation doesn't complete)
                // This ensures ghost units are removed even if the animation system doesn't clean them up
                setTimeout(() => {
                  if (ghostUnitsRef.current.has(slotKey)) {
                    const ghostData = ghostUnitsRef.current.get(slotKey);
                    // Only remove if it's still the same backend ghost (not replaced by frontend ghost)
                    if (ghostData && ghostData.animationKey === backendAnimationKey) {
                      ghostUnitsRef.current.delete(slotKey);
                      
                      // Also clean up the targetInstanceId tracking
                      if (ghostData.targetInstanceId) {
                        // Note: We can't access targetedUnitInstanceIdsRef here, but it will be cleaned up by completeAnimation
                      }
                    }
                  }
                }, 5000); // 5 seconds - enough time for animation to complete, but not too long
              }
            }
          }
        });
      }
    }

    // ==========================================
    // DETECT PLAYER STRATEGEM 185/188/220 DAMAGE ON OPPONENT GENERAL (SPECIAL CASE)
    // ==========================================
    // Check for player strategem 185 (Shadow Bargain), 188 (Essence Siphon), or 220 (Plague of Water turned to Blood) damage on opponent general
    // This needs to be detected separately because damage is on opponent's generalInteractions
    const opponentIdForDamage = Object.keys(gameState.state).find(id => String(id) !== String(userId));
    if (opponentIdForDamage) {
      const opponentState = gameState.state[opponentIdForDamage];
      if (opponentState?.generalInteractions && Array.isArray(opponentState.generalInteractions)) {
        opponentState.generalInteractions.forEach((interaction, index) => {
          // Only process strategem 185, 188, or 220 damage on opponent general from player's strategem
          if (interaction.sourceType !== 'strategem' || interaction.effectType !== 'damage' || (interaction.sourceId !== 185 && interaction.sourceId !== 188 && interaction.sourceId !== 220)) {
            return;
          }

          
          // Check if this strategem is in player's completedOrders (was played by player)
          const playerState = gameState.state[userId];
          const strategemInCompleted = playerState?.completedOrders?.some(
            order => order && order.instanceId === interaction.sourceInstanceId
          );
          
          
          if (!strategemInCompleted) {
            // This strategem wasn't played by the player, skip it (will be handled in opponent section)
            return;
          }

          // Create unique key for this interaction
          const interactionKey = `player-damage-185-${interaction.sourceId}-${interaction.sourceInstanceId}-${interaction.interactionTurn}-${index}`;
          
          if (processedEffectsRef.current.has(interactionKey)) {
            return; // Already processed
          }

          processedEffectsRef.current.add(interactionKey);
          animationCounterRef.current += 1;

          // Get strategem card position from hand (same method as targetStrategem.js)
          const cardElement = document.querySelector(`[data-instance-id="${interaction.sourceInstanceId}"]`);
          let fromPosition = null;
          
          if (cardElement) {
            const rect = cardElement.getBoundingClientRect();
            fromPosition = {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2
            };
          } else {
            // Fallback: approximate hand position (center-bottom of screen)
            fromPosition = { x: window.innerWidth / 2, y: window.innerHeight - 100 };
          }

          // Register black orb animation for damage on opponent general
          const damageAnimationKey = `player-damage-strategem-${interaction.sourceId}-${interaction.sourceInstanceId}-${animationCounterRef.current}-${Date.now()}`;
          registerAnimation({
            fromHand: { 
              instanceId: interaction.sourceInstanceId, 
              position: fromPosition 
            },
            toSlot: { 
              zone: 'general', 
              index: null, 
              playerId: 'opponent' // Opponent's general takes damage
            },
            targetType: 'general',
            strategemId: interaction.sourceId,
            strategemInstanceId: interaction.sourceInstanceId,
            animationKey: damageAnimationKey,
            isOpponentStrategem: false, // Player's own strategem
            effectType: 'damage' // Black orb for damage
          });

        });
      }
    }

    // ==========================================
    // DETECT PLAYER STRATEGEM HEALING EFFECTS ON PLAYER GENERAL (POSITIVE EFFECTS)
    // ==========================================
    // Check for player strategems that heal the player's own general
    // This handles strategems like 56 (Viral Echo) that heal the strategem player's general
    // Special case: Strategem 185 (Shadow Bargain), 188 (Essence Siphon), or 220 (Plague of Water turned to Blood) has both damage and heal effects
    // When player plays such a strategem, we show animations from hand to generals
    // Uses generalInteractions (same as useStrategemGeneralDamage uses for damage detection)
    const playerState = gameState.state[userId];
    if (playerState?.generalInteractions && Array.isArray(playerState.generalInteractions)) {
      playerState.generalInteractions.forEach((interaction, index) => {
        // Only process strategem healing effects on player's general from player's own strategems
        if (interaction.sourceType !== 'strategem' || interaction.effectType !== 'heal') {
          return;
        }

        // Special case: Strategem 185 (Shadow Bargain), 188 (Essence Siphon), or 220 - check if we also have damage interaction
        // If so, we'll register both animations together
        const isDualEffectStrategem = interaction.sourceId === 185 || interaction.sourceId === 188 || interaction.sourceId === 220;
        let damageInteraction = null;
        
        if (isDualEffectStrategem) {
          // Check opponent's general interactions for damage from the same strategem
          const opponentId = Object.keys(gameState.state).find(id => String(id) !== String(userId));
          if (opponentId) {
            const opponentState = gameState.state[opponentId];
            if (opponentState?.generalInteractions && Array.isArray(opponentState.generalInteractions)) {
              damageInteraction = opponentState.generalInteractions.find(
                di => di.sourceType === 'strategem' && 
                      di.effectType === 'damage' && 
                      di.sourceInstanceId === interaction.sourceInstanceId
              );
              if (damageInteraction) {
              } else {
              }
            }
          }
          // For strategem 185, 188, or 220, always register both animations even if damage interaction not found yet
          // The damage interaction will be added to opponent's state, and we'll handle it there
        }

        // Create unique key for this interaction
        const interactionKey = `player-heal-${interaction.sourceId}-${interaction.sourceInstanceId}-${interaction.interactionTurn}-${index}`;
        
        if (processedEffectsRef.current.has(interactionKey)) {
          return; // Already processed
        }

        // Check if this strategem is in completedOrders (was played by player)
        // This ensures we only animate player's own strategems, not opponent's
        const strategemInCompleted = playerState.completedOrders?.some(
          order => order && order.instanceId === interaction.sourceInstanceId
        );
        
        if (!strategemInCompleted) {
          // This strategem wasn't played by the player, skip it
          return;
        }

        processedEffectsRef.current.add(interactionKey);
        animationCounterRef.current += 1;
        const animationKey = `player-heal-strategem-${interaction.sourceInstanceId}-${animationCounterRef.current}-${Date.now()}`;

        // Get strategem card position from hand (same method as targetStrategem.js)
        // Try to find the strategem card by instanceId
        const cardElement = document.querySelector(`[data-instance-id="${interaction.sourceInstanceId}"]`);
        let fromPosition = null;
        
        if (cardElement) {
          const rect = cardElement.getBoundingClientRect();
          fromPosition = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
        } else {
          // Fallback: approximate hand position (center-bottom of screen) - same as targetStrategem.js
          fromPosition = { x: window.innerWidth / 2, y: window.innerHeight - 100 };
        }
        
        // Special handling for strategem 185 or 188: Register heal animation only
        // Damage animation is handled separately in the damage detection section above
        if (isDualEffectStrategem) {
          
          // Yellow orb to player general (heal effect)
          registerAnimation({
            fromHand: { 
              instanceId: interaction.sourceInstanceId, 
              position: fromPosition 
            },
            toSlot: { 
              zone: 'general', 
              index: null, 
              playerId: 'your' // Player's general is being healed
            },
            targetType: 'general',
            strategemId: interaction.sourceId,
            strategemInstanceId: interaction.sourceInstanceId,
            animationKey,
            isOpponentStrategem: false, // Player's own strategem
            effectType: 'heal' // Yellow orb for healing
          });
          
        } else {
          // Regular healing animation (single yellow orb)
          registerAnimation({
            fromHand: { 
              instanceId: interaction.sourceInstanceId, 
              position: fromPosition 
            },
            toSlot: { 
              zone: 'general', 
              index: null, 
              playerId: 'your' // Player's general is being healed
            },
            targetType: 'general',
            strategemId: interaction.sourceId,
            strategemInstanceId: interaction.sourceInstanceId,
            animationKey,
            isOpponentStrategem: false, // Player's own strategem
            effectType: 'heal' // Yellow orb for healing
          });

        }
      });
    }

    // ==========================================
    // DETECT OPPONENT STRATEGEM 185/188/220 DAMAGE ON PLAYER GENERAL (SPECIAL CASE)
    // ==========================================
    // Check for opponent strategem 185 (Shadow Bargain), 188 (Essence Siphon), or 220 (Plague of Water turned to Blood) damage on player general
    // This needs to be detected separately because damage is on player's generalInteractions
    const playerStateForDamage = gameState.state[userId];
    if (playerStateForDamage?.generalInteractions && Array.isArray(playerStateForDamage.generalInteractions)) {
      playerStateForDamage.generalInteractions.forEach((interaction, index) => {
          // Only process strategem 185, 188, or 220 damage on player general from opponent's strategem
          if (interaction.sourceType !== 'strategem' || interaction.effectType !== 'damage' || (interaction.sourceId !== 185 && interaction.sourceId !== 188 && interaction.sourceId !== 220)) {
            return;
          }

        // Check if this strategem is NOT in player's completedOrders (was played by opponent)
        const strategemInPlayerCompleted = playerStateForDamage?.completedOrders?.some(
          order => order && order.instanceId === interaction.sourceInstanceId
        );
        
        if (strategemInPlayerCompleted) {
          // This strategem was played by the player, skip it (will be handled in player section)
          return;
        }

        // Create unique key for this interaction
        const interactionKey = `opponent-damage-185-188-220-${interaction.sourceId}-${interaction.sourceInstanceId}-${interaction.interactionTurn}-${index}`;
        
        if (processedEffectsRef.current.has(interactionKey)) {
          return; // Already processed
        }

        processedEffectsRef.current.add(interactionKey);
        animationCounterRef.current += 1;

        const fromPosition = getOpponentGeneralPosition();

        // Register black orb animation for damage on player general
        const damageAnimationKey = `opponent-damage-strategem-${interaction.sourceId}-${interaction.sourceInstanceId}-${animationCounterRef.current}-${Date.now()}`;
        registerAnimation({
          fromHand: { 
            instanceId: interaction.sourceInstanceId, 
            position: fromPosition 
          },
          toSlot: { 
            zone: 'general', 
            index: null, 
            playerId: 'your' // Player's general takes damage
          },
          targetType: 'general',
          strategemId: interaction.sourceId,
          strategemInstanceId: interaction.sourceInstanceId,
          animationKey: damageAnimationKey,
          isOpponentStrategem: true,
          effectType: 'damage' // Black orb for damage
        });

      });
    }

    // ==========================================
    // DETECT OPPONENT STRATEGEM HEALING EFFECTS (POSITIVE EFFECTS)
    // ==========================================
    // Check for opponent strategems that heal the opponent's general
    // This handles strategems like 56 (Viral Echo) that heal the strategem player's own general
    // Special case: Strategem 185 (Shadow Bargain), 188 (Essence Siphon), or 220 (Plague of Water turned to Blood) has both damage and heal effects
    // When opponent plays such a strategem, we show a yellow orb animation from opponent general to opponent general
    const opponentId = Object.keys(gameState.state).find(id => String(id) !== String(userId));
    if (opponentId) {
      const opponentState = gameState.state[opponentId];
      
      // Check opponent's general interactions (opponent strategem healing opponent's own general)
      // This handles strategems like 56 (Viral Echo) that heal the strategem player's own general
      if (opponentState?.generalInteractions && Array.isArray(opponentState.generalInteractions)) {
        opponentState.generalInteractions.forEach((interaction, index) => {
          // Only process strategem healing effects on opponent's general
          if (interaction.sourceType !== 'strategem' || interaction.effectType !== 'heal') {
            return;
          }

          // Special case: Strategem 185 (Shadow Bargain), 188 (Essence Siphon), or 220 (Plague of Water turned to Blood) - damage is handled separately
          const isDualEffectStrategem = interaction.sourceId === 185 || interaction.sourceId === 188 || interaction.sourceId === 220;

          // Create unique key for this interaction
          const interactionKey = `opponent-heal-${interaction.sourceId}-${interaction.sourceInstanceId}-${interaction.interactionTurn}-${index}`;
          
          if (processedEffectsRef.current.has(interactionKey)) {
            return; // Already processed
          }

          processedEffectsRef.current.add(interactionKey);
          animationCounterRef.current += 1;
          const animationKey = `opponent-heal-strategem-${interaction.sourceInstanceId}-${animationCounterRef.current}-${Date.now()}`;

          const fromPosition = getOpponentGeneralPosition();
          
          // Special handling for strategem 185 or 188: Register heal animation only
          // Damage animation is handled separately in the damage detection section above
          if (isDualEffectStrategem) {
            
            // Yellow orb to opponent general (heal effect)
            registerAnimation({
              fromHand: { 
                instanceId: interaction.sourceInstanceId, 
                position: fromPosition 
              },
              toSlot: { 
                zone: 'general', 
                index: null, 
                playerId: 'opponent' // Opponent's general is being healed
              },
              targetType: 'general',
              strategemId: interaction.sourceId,
              strategemInstanceId: interaction.sourceInstanceId,
              animationKey,
              isOpponentStrategem: true,
              effectType: 'heal' // Yellow orb for healing
            });
            
          } else {
            // Regular healing animation (single yellow orb)
            registerAnimation({
              fromHand: { 
                instanceId: interaction.sourceInstanceId, 
                position: fromPosition 
              },
              toSlot: { 
                zone: 'general', 
                index: null, 
                playerId: 'opponent' // Opponent's general is being healed
              },
              targetType: 'general',
              strategemId: interaction.sourceId,
              strategemInstanceId: interaction.sourceInstanceId,
              animationKey,
              isOpponentStrategem: true,
              effectType: 'heal' // Yellow orb for healing
            });

          }
        });
      }
    }

    // ==========================================
    // FALLBACK: DETECT OPPONENT STRATEGEMS FROM ACTION HISTORY
    // ==========================================
    if (!actionHistory) {
      return;
    }

    // Find opponent strategem-requested actions
    const opponentStrategemActions = actionHistory.filter(action => {
      if (action.type !== 'strategem-requested') return false;
      // Check if this is from opponent (not from current player)
      return String(action.playerId) !== String(userId);
    });

    if (opponentStrategemActions.length === 0) {
      return;
    }

    const fromPosition = getOpponentGeneralPosition();

    opponentStrategemActions.forEach(action => {
      const strategem = action.actionItem || action.order;
      if (!strategem) return;

      // Create unique key for this strategem play
      const effectKey = `strategem-${strategem.instanceId}-${action.timestamp}`;
      
      if (processedEffectsRef.current.has(effectKey)) {
        return; // Already processed
      }
      
      processedEffectsRef.current.add(effectKey);
      
      const targets = action.targets || [];
      
      // If no targets, register animation to a default position
      if (targets.length === 0) {
        animationCounterRef.current += 1;
        const animationKey = `opponent-strategem-${strategem.instanceId}-${animationCounterRef.current}-${Date.now()}`;
        
        registerAnimation({
          fromHand: { 
            instanceId: strategem.instanceId, 
            position: fromPosition 
          },
          toSlot: { 
            zone: 'center', 
            index: null, 
            playerId: 'your'
          },
          targetType: 'zone',
          strategemId: strategem.id,
          strategemInstanceId: strategem.instanceId,
          animationKey,
          isOpponentStrategem: true
        });
        return;
      }

      // Register animation for each target
      targets.forEach((target, targetIndex) => {
        animationCounterRef.current += 1;
        const animationKey = `opponent-strategem-${strategem.instanceId}-${targetIndex}-${animationCounterRef.current}-${Date.now()}`;

        if (target.type === 'general') {
          // Target is player's general
          registerAnimation({
            fromHand: { 
              instanceId: strategem.instanceId, 
              position: fromPosition 
            },
            toSlot: { 
              zone: 'general', 
              index: null, 
              playerId: 'your'
            },
            targetType: 'general',
            strategemId: strategem.id,
            strategemInstanceId: strategem.instanceId,
            animationKey,
            isOpponentStrategem: true
          });
        } else if (target.instanceId && target.zone !== undefined && target.zone !== null) {
          // Target is a unit with a valid zone
          // ✅ FIX: Zone should already have correct prefix from actionDetection.js
          // But handle cases where it might not (backwards compatibility)
          let targetZone = target.zone;
          let targetPlayerId = 'your'; // Default to 'your' for opponent strategems (opponent targets player's units)
          
          // If zone already has prefix, extract playerId from zone
          if (targetZone && targetZone.includes('_')) {
            if (targetZone.startsWith('opponent_')) {
              targetPlayerId = 'opponent';
            } else if (targetZone.startsWith('your_')) {
              targetPlayerId = 'your';
            }
          } else if (targetZone && !targetZone.includes('_')) {
            // Zone is just 'front' or 'flank' without prefix (backwards compatibility)
            // For opponent strategems, if target.playerId exists, use it to determine
            // Otherwise default to 'your' (opponent targeting player's units)
            if (target.playerId) {
              // If target belongs to current user, it's 'your', otherwise it's 'opponent'
              targetPlayerId = String(target.playerId) === String(userId) ? 'your' : 'opponent';
              targetZone = `${targetPlayerId}_${targetZone}`;
            } else {
              // No playerId info, default to 'your' for opponent strategems
              targetZone = `your_${targetZone}`;
              targetPlayerId = 'your';
            }
          }
          
          // Store ghost unit if the unit died
          if (target.died && ghostUnitsRef) {
            const slotKey = `${targetZone}_${target.index}`;
            ghostUnitsRef.current.set(slotKey, {
              unit: {
                id: target.id,
                name: target.name,
                instanceId: target.instanceId
              },
              animationKey: animationKey,
              targetInstanceId: target.instanceId
            });
          }
          
          registerAnimation({
            fromHand: { 
              instanceId: strategem.instanceId, 
              position: fromPosition 
            },
            toSlot: { 
              zone: targetZone, 
              index: target.index, 
              playerId: targetPlayerId
            },
            targetType: 'unit',
            strategemId: strategem.id,
            strategemInstanceId: strategem.instanceId,
            targetInstanceId: target.instanceId,
            ghostUnit: target.died ? {
              id: target.id,
              name: target.name,
              instanceId: target.instanceId
            } : null,
            animationKey,
            isOpponentStrategem: true
          });
        }
      });
    });

    // Clean up old processed effects (keep last 100)
    if (processedEffectsRef.current.size > 100) {
      const keys = Array.from(processedEffectsRef.current);
      keys.slice(0, keys.length - 100).forEach(key => {
        processedEffectsRef.current.delete(key);
      });
    }
  }, [actionHistory, gameState, userId, registerAnimation, getOpponentGeneralPosition, ghostUnitsRef]);
};

export default useOpponentStrategemAnimations;
